import { supabase } from "@/lib/supabase";
import type { BodyRegionFocus, BundleTemplateRow } from "@/types/database";

export interface ListTemplatesFilters {
  is_active?: boolean;
  body_region_focus?: BodyRegionFocus;
  search?: string;
}

export async function listTemplates(filters?: ListTemplatesFilters): Promise<BundleTemplateRow[]> {
  let q = supabase.from("bundle_templates").select("*").order("created_at", { ascending: false });
  if (typeof filters?.is_active === "boolean") q = q.eq("is_active", filters.is_active);
  if (filters?.body_region_focus) q = q.eq("body_region_focus", filters.body_region_focus);
  if (filters?.search?.trim()) q = q.ilike("title", `%${filters.search.trim()}%`);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as BundleTemplateRow[];
}

export async function getTemplate(id: string): Promise<BundleTemplateRow | null> {
  const { data, error } = await supabase
    .from("bundle_templates")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as BundleTemplateRow | null;
}

export interface CreateTemplateInput {
  title: string;
  description: string | null;
  body_region_focus: BodyRegionFocus;
  exercise_ids: string[];
  total_duration: number | null;
  is_active: boolean;
  created_by: string | null;
}

export async function createTemplate(input: CreateTemplateInput): Promise<BundleTemplateRow> {
  const { data, error } = await supabase
    .from("bundle_templates")
    .insert(input as never)
    .select()
    .single();
  if (error) throw error;
  return data as BundleTemplateRow;
}

export type UpdateTemplatePatch = Partial<
  Pick<
    BundleTemplateRow,
    "title" | "description" | "body_region_focus" | "exercise_ids" | "total_duration" | "is_active"
  >
>;

export async function updateTemplate(
  id: string,
  patch: UpdateTemplatePatch,
): Promise<BundleTemplateRow> {
  const { data, error } = await supabase
    .from("bundle_templates")
    .update(patch as never)
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data as BundleTemplateRow;
}

export async function deleteTemplate(id: string): Promise<void> {
  const { error } = await supabase.from("bundle_templates").delete().eq("id", id);
  if (error) throw error;
}
